import {ReactElement, useEffect, useState} from 'react';
import {Box, SelectChangeEvent} from '@mui/material';
import {useTranslation} from 'react-i18next';
import LoadingIndicator from 'components/shared/ui/LoadingIndicator';
import Toast from 'components/shared/ui/Toast';
import {ApiMutationStatus, ApiQueryStatus} from 'interfaces/ApiRequestStatus';
import {Project} from 'interfaces/Project';
import {System} from 'interfaces/System';
import ProjectConfigurator from './components/ProjectConfigurator';
import ProjectSidebar from './components/ProjectSidebar';
import {UseConfiguratorActions} from './hooks/useConfigurator';
import {UseSaveProject} from './hooks/useConfigurator/useSaveProject';

type Props = {
  selectProjectForm: {
    value: string | null;
    options: Project[];
  };
  onSelectProject: (event: SelectChangeEvent) => void;
  selectFirstProject: () => void;
  editableProject: Project | null;
  systems: System[];
  configuratorActions: UseConfiguratorActions;
  getProjectStatus: ApiQueryStatus;
  saveProject: UseSaveProject;
  onCreate: () => void;
  createProjectRequestStatus: ApiMutationStatus;
};

export default function CalculatorView({
  selectProjectForm,
  onSelectProject,
  selectFirstProject,
  editableProject,
  systems,
  configuratorActions,
  getProjectStatus,
  saveProject,
  onCreate,
  createProjectRequestStatus
}: Props): ReactElement {
  const {t} = useTranslation('common');
  const [isErrorSnackbarVisible, setIsErrorSnackbarVisible] = useState(false);
  const [isSuccessSnackbarVisible, setIsSuccessSnackbarVisible] = useState(false);

  function handleCloseErrorSnackbar() {
    setIsErrorSnackbarVisible(false);
  }

  function handleCloseSuccessSnackbar() {
    setIsSuccessSnackbarVisible(false);
  }

  useEffect(
    function () {
      if (saveProject.requestStatus.isError) {
        setIsErrorSnackbarVisible(true);
      }
    },
    [saveProject.requestStatus.isError]
  );

  useEffect(
    function () {
      if (saveProject.requestStatus.isSuccess) {
        setIsSuccessSnackbarVisible(true);
      }
    },
    [saveProject.requestStatus.isSuccess]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: {xs: 'column', md: 'row'},
        alignItems: 'flex-start',
        gap: {xs: 2, md: '10px'},
        py: 5
      }}
    >
      <Box sx={{width: {xs: '100%', md: 284}, flexShrink: 0}}>
        <ProjectSidebar
          t={t}
          selectProjectForm={selectProjectForm}
          onSelectProject={onSelectProject}
          onCreate={onCreate}
        />
      </Box>
      <Box sx={{flexGrow: 1, width: '100%', minWidth: 0}}>
        {editableProject && !getProjectStatus.fetchStatus.isFetching && (
          <ProjectConfigurator
            editableProject={editableProject}
            systems={systems}
            saveProject={saveProject}
            configuratorActions={configuratorActions}
            onProjectAction={selectFirstProject}
          />
        )}
        {getProjectStatus.dataStatus.isLoading && (
          <LoadingIndicator centered text={t('common:loadingProject')} />
        )}
        {createProjectRequestStatus.isLoading && (
          <LoadingIndicator text={t('common:creatingProject')} />
        )}
      </Box>
      <Toast
        open={isErrorSnackbarVisible}
        severity="error"
        message={t('common:saveProjectError')}
        onClose={handleCloseErrorSnackbar}
      />
      <Toast
        open={isSuccessSnackbarVisible}
        severity="success"
        message={t('common:savedSuccessfully')}
        onClose={handleCloseSuccessSnackbar}
      />
    </Box>
  );
}
